import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { ZoomControls, type ViewTabOption } from './ZoomControls';
import {
  TooltipComponent,
  buildPartInfoFromRowData,
  findSelectedPartDetail,
  getPartImageFields,
  getSelectedPartRow,
  type PartInfo,
} from './partssvgviewer';
import {
  buildCoordinateLookup,
  getCoordinateForReference,
} from './partsCoordinateUtils';

interface PartsImageViewerProps {
  imageUrl: string;
  isFileViewer?: boolean;
  currentActivePartsRowData?: any;
  setSelectedRowId?: any;
  filename?: string;
  setSvgHighlightData?: any;
  coordinates?: any[];
  componentDetails?: any[];
  highlightedComponent?: any;
  onImagePartClick?: (componentDetail: any) => void;
  highlightedReferenceNumbers?: string[];
  catalogSourceId?: string;
  partsRowSelectToken?: number;
  // Multi-image nav: only rendered when the component has more than one diagram.
  onPrevImage?: () => void;
  onNextImage?: () => void;
  canGoPrevImage?: boolean;
  canGoNextImage?: boolean;
  // "Component Group" / "Component Name" segmented toggle, rendered inline in the
  // zoom-controls toolbar. Only rendered when provided.
  viewTabs?: ViewTabOption[];
  activeViewTabKey?: string;
  onViewTabChange?: (key: string) => void;
}

const PartsImageViewer: React.FC<PartsImageViewerProps> = ({
  imageUrl,
  isFileViewer,
  currentActivePartsRowData,
  setSelectedRowId,
  filename,
  setSvgHighlightData,
  coordinates,
  componentDetails,
  highlightedComponent,
  onImagePartClick,
  highlightedReferenceNumbers,
  catalogSourceId,
  partsRowSelectToken,
  onPrevImage,
  onNextImage,
  canGoPrevImage,
  canGoNextImage,
  viewTabs,
  activeViewTabKey,
  onViewTabChange,
}) => {
  const [imageScale, setImageScale] = useState(isFileViewer ? 1 : 0.85);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [tooltipData, setTooltipData] = useState<PartInfo | null>(null);
  const [selectedReferenceNumber, setSelectedReferenceNumber] = useState<string | null>(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const MIN_SCALE = 0.1;
  const MAX_SCALE = 10;
  const ZOOM_STEP = 0.1;

  // Reset error state when imageUrl changes
  useEffect(() => {
    setImageLoadError(false);
    setImageLoaded(false);
    setSelectedReferenceNumber(null); // Reset selection when image changes
    // Clear any tooltip anchored to the previous image's geometry — otherwise it
    // stays visible (computed against the old/soon-stale <img> dimensions) while
    // the new image loads, and briefly jumps to a wrong spot once coordinates
    // for the new diagram are applied.
    setTooltipData(null);
  }, [imageUrl]);

  // Add resize observer to track container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Force re-render of coordinate dots when container size changes
  useEffect(() => {
    if (containerSize.width > 0 && imageLoaded) {
      // This will trigger a re-render of the coordinate dots
    }
  }, [containerSize, imageLoaded]);

  const handleImageError = () => {
    setImageLoadError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageZoomIn = () => {
    setImageScale(prev => Math.min(prev + ZOOM_STEP, MAX_SCALE));
  };

  const handleImageZoomOut = () => {
    setImageScale(prev => Math.max(prev - ZOOM_STEP, MIN_SCALE));
  };

  const handleImageReset = () => {
    setImageScale(isFileViewer ? 1 : 0.85);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isDragging) {
      setImagePosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY;
    const zoomFactor = 0.0005;
    setImageScale((prev) => {
      const newScale = prev * (1 - delta * zoomFactor);
      return Math.min(Math.max(newScale, MIN_SCALE), MAX_SCALE);
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    dragStart.current = {
      x: touch.clientX - imagePosition.x,
      y: touch.clientY - imagePosition.y,
    };
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging) {
      const touch = e.touches[0];
      setImagePosition({
        x: touch.clientX - dragStart.current.x,
        y: touch.clientY - dragStart.current.y,
      });
    }
  };

  // Memoize coordinate lookup: exact match first, then partial match
  const coordinateLookup = useMemo(
    () => buildCoordinateLookup(coordinates ?? []),
    [coordinates]
  );

  const uniqueCoordinates = useMemo(() => {
    let entries = Array.from(coordinateLookup.values());
    if (highlightedReferenceNumbers?.length) {
      entries = entries.filter((coord) =>
        highlightedReferenceNumbers.includes(coord.text ?? '')
      );
    }
    return entries;
  }, [coordinateLookup, highlightedReferenceNumbers]);

  const getMatchingCoordinate = useCallback(
    (referenceNumber: string) =>
      getCoordinateForReference(coordinateLookup, String(referenceNumber).trim()),
    [coordinateLookup]
  );

  const selectedPart = getSelectedPartRow(currentActivePartsRowData);

  // A string key, so effects re-run only when the selected part actually changes.
  const activeRowKey = useMemo(() => {
    if (!selectedPart) return '';
    return [
      selectedPart.source_id,
      selectedPart.file_name,
      selectedPart.component_group,
      selectedPart.component_name,
      selectedPart.referenceNumber,
      selectedPart.parts_number,
      selectedPart.name,
    ]
      .map((value) => String(value ?? '').trim())
      .join(':');
  }, [selectedPart]);

  const calculateCoordinatePosition = (coord: any): { x: number; y: number } | null => {
    if (!containerRef.current || !coord?.polygon?.length) return null;

    const imgElement = containerRef.current?.querySelector('img') as HTMLImageElement;
    if (!imgElement) return null;

    // Calculate center point of the polygon
    const centerX = coord.polygon.reduce((sum: number, point: number[]) => sum + point[0], 0) / coord.polygon.length;
    const centerY = coord.polygon.reduce((sum: number, point: number[]) => sum + point[1], 0) / coord.polygon.length;

    // Get dimensions and calculate scale (same logic as renderCoordinateDot)
    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;
    // While a new `src` is loading, naturalWidth/Height are momentarily 0 — bail out
    // instead of dividing by zero and producing NaN/Infinity coordinates.
    if (!naturalWidth || !naturalHeight) return null;
    const displayWidth = imgElement.offsetWidth;
    const displayHeight = imgElement.offsetHeight;
    const scaleX = displayWidth / naturalWidth;
    const scaleY = displayHeight / naturalHeight;

    // Map coordinates to display space
    const mappedX = centerX * scaleX;
    const mappedY = centerY * scaleY;

    // Get transformed image position (getBoundingClientRect accounts for CSS transform)
    const imgRect = imgElement.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Calculate position: image position + mapped coordinates (buttons use same logic)
    // Since buttons are inside transformed div, we need to account for transform
    // The mapped coordinates are relative to the untransformed image, so we apply transform
    const imgCenterX = displayWidth / 2;
    const imgCenterY = displayHeight / 2;
    const offsetX = mappedX - imgCenterX;
    const offsetY = mappedY - imgCenterY;

    // Apply CSS transform (scale around center, then translate)
    const transformedX = (imgRect.width / 2) + (offsetX * imageScale) + (imagePosition.x * imageScale);
    const transformedY = (imgRect.height / 2) + (offsetY * imageScale) + (imagePosition.y * imageScale);

    return {
      x: (imgRect.left - containerRect.left) + transformedX,
      y: (imgRect.top - containerRect.top) + transformedY
    };
  };

  // Handle coordinate dot click
  const handleDotClick = (coord: any, index: number, event?: React.MouseEvent<HTMLButtonElement>) => {
    if (!componentDetails || !coord.text) return;

    // Normalize both sides before comparing: `coord.text` (hotspot OCR text) is always
    // a trimmed string, but `referenceNumber` on a component-details row can come back
    // as a number or with stray whitespace depending on the endpoint/page it was fetched
    // from — a strict `===` silently drops the match (and the click does nothing).
    const targetRef = String(coord.text).trim();
    const matchingComponent = componentDetails.find((detail: any) =>
      String(detail.referenceNumber ?? '').trim() === targetRef
    );

    if (!matchingComponent) return;

    // Set selected reference number for persistent highlighting
    setSelectedReferenceNumber(String(coord.text).trim());

    // Calculate position relative to container for tooltip
    const calculateAndSetPosition = () => {
      if (!containerRef.current) return;

      let x: number;
      let y: number;

      if (event?.currentTarget) {
        // Get the button element's position
        const buttonRect = event.currentTarget.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        x = buttonRect.left - containerRect.left + (buttonRect.width / 2);
        y = buttonRect.top - containerRect.top + (buttonRect.height / 2);
      } else {
        // Fallback: use calculateCoordinatePosition helper
        const position = calculateCoordinatePosition(coord);
        if (!position) return;
        x = position.x;
        y = position.y;
      }

      const toolTipData: PartInfo = {
        parts_number: isFileViewer ? matchingComponent.parts_number : matchingComponent.parts_number ?? '',
        description: isFileViewer ? matchingComponent.name : matchingComponent.name ?? '',
        ref_number: String(coord.text).trim(),
        x,
        y,
        ...getPartImageFields(matchingComponent),
      };

      if (isFileViewer && setSelectedRowId) {
        setSelectedRowId(matchingComponent);
      } else if (onImagePartClick) {
        onImagePartClick(matchingComponent);
      }

      setTooltipData(toolTipData);
    };

    if (event) {
      requestAnimationFrame(calculateAndSetPosition);
    } else {
      calculateAndSetPosition();
    }
  };

  // Recalculate tooltip position when zoom/pan changes
  useEffect(() => {
    if (!tooltipData?.ref_number) {
      if (!tooltipData?.x || !tooltipData?.y || !activeRowKey || !selectedPart) return;

      const refNumber = selectedPart.referenceNumber;
      if (!refNumber) return;

      const matchingCoord = getMatchingCoordinate(String(refNumber));

      if (matchingCoord) {
        const position = calculateCoordinatePosition(matchingCoord);
        if (position) {
          setTooltipData((prev) => {
            if (!prev) return null;
            if (
              Math.abs((prev.x ?? 0) - position.x) < 0.5 &&
              Math.abs((prev.y ?? 0) - position.y) < 0.5
            ) {
              return prev;
            }
            return { ...prev, x: position.x, y: position.y };
          });
        }
      }
      return;
    }

    if (!containerRef.current) return;

    const matchingCoord = getMatchingCoordinate(String(tooltipData.ref_number));
    if (!matchingCoord) return;

    const position = calculateCoordinatePosition(matchingCoord);
    if (!position) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const nextAnchorX = containerRect.left + position.x;
    const nextAnchorY = containerRect.top + position.y;

    setTooltipData((prev) => {
      if (!prev) return null;
      if (
        Math.abs((prev.anchorClientX ?? 0) - nextAnchorX) < 0.5 &&
        Math.abs((prev.anchorClientY ?? 0) - nextAnchorY) < 0.5
      ) {
        return prev;
      }
      return {
        ...prev,
        anchorClientX: nextAnchorX,
        anchorClientY: nextAnchorY,
      };
    });
  }, [
    imageScale,
    imagePosition,
    imageLoaded,
    tooltipData?.ref_number,
    tooltipData?.x,
    tooltipData?.y,
    activeRowKey,
    isFileViewer,
    getMatchingCoordinate,
  ]);

  useEffect(() => {
    if (!activeRowKey) {
      setTooltipData(null);
      return;
    }

    const calculateAndSetPosition = () => {
      if (!containerRef.current) return;

      const toolTipData = buildPartInfoFromRowData(currentActivePartsRowData, componentDetails);
      if (!toolTipData) return;

      // A part row can carry no reference number at all (part-level rows with only
      // an image): keep going so the tooltip still opens, just without a hotspot.
      const refNumber = String(toolTipData.ref_number ?? '').trim();
      if (refNumber) {
        setSelectedReferenceNumber((prev) => (prev === refNumber ? prev : refNumber));
      }

      const matchingCoord = refNumber ? getMatchingCoordinate(refNumber) : null;

      const applyTooltip = (anchorClientX: number, anchorClientY: number) => {
        setTooltipData((prev) => {
          if (
            prev?.ref_number === refNumber &&
            prev?.parts_number === toolTipData.parts_number &&
            prev?.description === toolTipData.description &&
            prev?.part_file_name === toolTipData.part_file_name &&
            Math.abs((prev.anchorClientX ?? 0) - anchorClientX) < 0.5 &&
            Math.abs((prev.anchorClientY ?? 0) - anchorClientY) < 0.5
          ) {
            return prev;
          }
          return {
            ...toolTipData,
            anchorClientX,
            anchorClientY,
          };
        });
      };

      if (matchingCoord) {
        // Check the live <img> directly rather than trusting the `imageLoaded` state:
        // when the image URL just changed, React can still be re-running this effect
        // with the previous render's (stale) `imageLoaded = true` for one pass while
        // the real <img> has already swapped to the new (not-yet-decoded) src.
        const imgElement = containerRef.current.querySelector('img') as HTMLImageElement | null;
        const isImageReady = !!imgElement && imgElement.naturalWidth > 0 && imgElement.naturalHeight > 0;
        if (!isImageReady) {
          // Wait for the image to finish loading — this effect re-runs once
          // `imageLoaded` flips true, and will compute the real position then
          // instead of showing a wrong interim spot.
          return;
        }

        const position = calculateCoordinatePosition(matchingCoord);
        if (position) {
          const containerRect = containerRef.current.getBoundingClientRect();
          applyTooltip(containerRect.left + position.x, containerRect.top + position.y);
          return;
        }
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      applyTooltip(
        containerRect.left + containerRect.width / 2,
        containerRect.top + containerRect.height / 2
      );
    };

    requestAnimationFrame(calculateAndSetPosition);
  }, [activeRowKey, isFileViewer, componentDetails, getMatchingCoordinate, imageLoaded, partsRowSelectToken]);

  const findBestMatchingCoordinate = () => {
    if (!selectedPart || !uniqueCoordinates?.length) return null;

    const rowRef = String(selectedPart.referenceNumber ?? '').trim();
    // The parts-catalog file viewer highlights the clicked row's own callout.
    // Chat highlights the catalog part's callout when that part was found,
    // which is how the image viewer already placed the dot.
    const matchedPart = isFileViewer
      ? undefined
      : findSelectedPartDetail(componentDetails, selectedPart);
    const refNumber = String(matchedPart?.referenceNumber ?? '').trim() || rowRef;
    if (!refNumber) return null;

    const textMatch = uniqueCoordinates.findIndex((coord) => coord.text === refNumber);

    return textMatch >= 0 ? textMatch : null;
  };

  // If image failed to load, return null to hide the entire container
  if (imageLoadError) {
    return null;
  }

  // Function to render a single coordinate dot
  const renderCoordinateDot = (coord: any, index: number, highlightedIndex: number | null) => {
    if (!coord.polygon || coord.polygon.length === 0) return null;

    // Calculate center point of the polygon
    const centerX = coord.polygon.reduce((sum: number, point: number[]) => sum + point[0], 0) / coord.polygon.length;
    const centerY = coord.polygon.reduce((sum: number, point: number[]) => sum + point[1], 0) / coord.polygon.length;

    // Get the image element to calculate proper scaling
    const imgElement = containerRef.current?.querySelector('img') as HTMLImageElement;
    if (!imgElement) return null;

    // Get the natural (original) dimensions of the image
    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;
    // While a new `src` is loading, naturalWidth/Height are momentarily 0 — skip
    // this dot rather than dividing by zero and placing it at a bogus position.
    if (!naturalWidth || !naturalHeight) return null;

    // Get the current displayed dimensions
    const displayWidth = imgElement.offsetWidth;
    const displayHeight = imgElement.offsetHeight;

    // Calculate the scale factors
    const scaleX = displayWidth / naturalWidth;
    const scaleY = displayHeight / naturalHeight;

    // Map the original coordinates to the displayed image coordinates
    const mappedX = centerX * scaleX;
    const mappedY = centerY * scaleY;

    // Create a unique key using coord text
    const uniqueKey = coord.text || `coord-${index}`;

    // Check if this coordinate should be highlighted
    const isHighlighted = selectedReferenceNumber
      ? coord.text === selectedReferenceNumber
      : highlightedIndex !== null && uniqueCoordinates[highlightedIndex]?.text === coord.text;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handleDotClick(coord, index, e);
    };

    return (
      <button
        key={uniqueKey}

        className={`absolute w-4 h-4 rounded-full cursor-pointer z-10 hover:border-2 hover:border-[#003a79] p-4 bg-[#003a7933] ${isHighlighted ? 'border-2 border-[#003a79]' : ''
          }`}

        style={{
          left: `${mappedX}px`,
          top: `${mappedY}px`,
          transform: 'translate(-50%, -50%)',
        }}
        onClick={handleClick}
        aria-label={`Component ${coord.text || index + 1}`}
      />
    );
  };

  // Function to render coordinate dots
  const renderCoordinateDots = () => {
    if (!uniqueCoordinates || uniqueCoordinates.length === 0 || !imageLoaded || containerSize.width === 0) return null;

    // Find the best matching coordinate index
    const highlightedIndex = findBestMatchingCoordinate();

    return uniqueCoordinates.map((coord, index) =>
      renderCoordinateDot(coord, index, highlightedIndex)
    );
  };

  // Function to render polygon highlights (invisible clickable areas)
  const renderPolygonHighlights = () => {
    if (!uniqueCoordinates || uniqueCoordinates.length === 0 || !imageLoaded || containerSize.width === 0) return null;

    const imgElement = containerRef.current?.querySelector('img') as HTMLImageElement;
    if (!imgElement) return null;

    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;
    const displayWidth = imgElement.offsetWidth;
    const displayHeight = imgElement.offsetHeight;

    if (naturalWidth === 0 || naturalHeight === 0) return null;

    const scaleX = displayWidth / naturalWidth;
    const scaleY = displayHeight / naturalHeight;

    const handlePolygonClick = (coord: any, e: React.MouseEvent<SVGPolygonElement>) => {
      e.stopPropagation();
      // Call handleDotClick without event - it will use fallback to calculate position from coordinates
      handleDotClick(coord, uniqueCoordinates.findIndex((c) => c.text === coord.text) ?? -1);
    };

    return (
      <svg
        className="absolute top-0 left-0"
        style={{
          width: `${displayWidth}px`,
          height: `${displayHeight}px`,
          pointerEvents: 'none',
        }}
      >
        {uniqueCoordinates.map((coord, index) => {
          if (!coord.polygon || coord.polygon.length === 0) return null;

          const points = coord.polygon
            .map((point: number[]) => `${point[0] * scaleX},${point[1] * scaleY}`)
            .join(' ');

          return (
            <polygon
              key={`polygon-${coord.text || index}`}
              points={points}
              fill="transparent"
              stroke="transparent"
              strokeWidth="0"
              className="cursor-pointer"
              style={{ pointerEvents: 'auto' }}
              onClick={(e) => handlePolygonClick(coord, e)}
            />
          );
        })}
      </svg>
    );
  };

  return (
    <div className="relative w-full h-full flex flex-col">
      <ZoomControls
        onZoomIn={handleImageZoomIn}
        onZoomOut={handleImageZoomOut}
        onReset={handleImageReset}
        isLoading={false}
        filename={filename}
        onPrev={onPrevImage}
        onNext={onNextImage}
        canGoPrev={canGoPrevImage}
        canGoNext={canGoNextImage}
        viewTabs={viewTabs}
        activeViewTabKey={activeViewTabKey}
        onViewTabChange={onViewTabChange}
      />

      <div
        ref={containerRef}
        className="flex-1 min-h-0 min-w-0 overflow-hidden relative touch-none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
        onTouchCancel={handleMouseUp}
      >
        <div
          style={{
            transform: `scale(${imageScale}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            cursor: isDragging ? 'grabbing' : 'grab',
            transformOrigin: 'center center',
            touchAction: 'none',
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
              src={imageUrl}
              alt={filename}
              className="w-auto h-auto object-contain"
              style={{
                // `max-h-full`/`max-w-full` (percentage-based) can't resolve here — this
                // element's immediate parent is `inline-block` with an auto (content-driven)
                // size, and per the CSS spec a percentage max-height/max-width against an
                // ancestor with no explicit size is ignored, letting the image render at its
                // full intrinsic pixel size instead of fitting the viewer. Cap it with the
                // container's actual measured pixel size (from the ResizeObserver above)
                // instead, so it always fits regardless of the image's native resolution.
                maxHeight: containerSize.height > 0 ? `${containerSize.height}px` : '100%',
                maxWidth: containerSize.width > 0 ? `${containerSize.width}px` : '100%',
              }}
              draggable="false"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            {renderPolygonHighlights()}
            {renderCoordinateDots()}
          </div>
        </div>
        <TooltipComponent
          tooltipData={tooltipData}
          setTooltipData={setTooltipData}
          setSelectedRowId={isFileViewer ? setSelectedRowId : undefined}
          catalogSourceId={catalogSourceId}
        />
      </div>
    </div>
  );
};

export default PartsImageViewer;
